import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { Shield, Mail } from 'lucide-react';
import './Legal.css';

const SECTIONS_PT = [
  {
    title: '1. Quem somos',
    content: 'A Libben Agents é uma plataforma de agentes digitais com IA operada pela FGMAX - INTERMEDIACAO DE NEGOCIOS EMPRESARIAIS E INFORMATICA LTDA, CNPJ 22.940.511/0001-26, com sede em Joinville/SC. Para questões de privacidade, entre em contato com privacidade@libben.com.br.'
  },
  {
    title: '2. Dados que coletamos',
    content: 'Coletamos dados fornecidos diretamente por você no cadastro (nome, e-mail, telefone, CNPJ/CPF, endereço de cobrança), dados de uso da plataforma (conversas processadas, agentes configurados, conectores utilizados), dados técnicos (endereço IP, tipo de navegador, logs de acesso) e dados de pagamento processados pelo gateway Asaas ou Stripe — não armazenamos dados brutos de cartão.'
  },
  {
    title: '3. Como usamos seus dados',
    content: 'Utilizamos seus dados para: prestação do serviço contratado, cobrança e gestão financeira, comunicações sobre o serviço (alertas, atualizações, faturas), suporte técnico, prevenção de fraudes e cumprimento de obrigações legais. Nos planos pagos, seus dados nunca são usados para treinar modelos de IA nem vendidos a terceiros.'
  },
  {
    title: '4. Dados dos seus clientes (usuários finais)',
    content: 'Você é o controlador dos dados dos seus clientes que interagem com os agentes. A FGMAX atua como operadora nos termos da LGPD (Lei 13.709/2018). Processamos esses dados apenas para executar o serviço contratado. Dados de sessão são excluídos automaticamente após 24 horas (TTL configurável).'
  },
  {
    title: '5. Compartilhamento de dados',
    content: 'Compartilhamos dados apenas com: provedores de infraestrutura (Amazon Web Services — armazenamento e processamento), gateways de pagamento (Asaas, Stripe), provedores de IA (Amazon Bedrock — processamento de mensagens sem retenção de dados), e quando exigido por lei ou ordem judicial. Não vendemos dados a terceiros.'
  },
  {
    title: '6. Segurança',
    content: 'Todos os dados são armazenados na Amazon Web Services (AWS) com criptografia em trânsito (TLS 1.2+) e em repouso (AES-256). Utilizamos autenticação via Amazon Cognito com MFA disponível. Realizamos auditorias de segurança periódicas e mantemos logs de acesso por 90 dias.'
  },
  {
    title: '7. Retenção de dados',
    content: 'Dados de sessão/conversa: excluídos automaticamente após 24 horas (TTL). Dados cadastrais e de faturamento: mantidos durante a vigência do contrato e por até 5 anos após o cancelamento para cumprimento de obrigações fiscais e legais. Você pode solicitar a exclusão antecipada pelo e-mail privacidade@libben.com.br.'
  },
  {
    title: '8. Seus direitos (LGPD)',
    content: 'Nos termos da Lei 13.709/2018, você tem direito a: confirmação da existência de tratamento, acesso aos dados, correção de dados incompletos ou desatualizados, anonimização ou bloqueio de dados desnecessários, portabilidade, eliminação dos dados tratados com consentimento, informação sobre compartilhamento, revogação do consentimento e oposição ao tratamento. Para exercer seus direitos, entre em contato com privacidade@libben.com.br.'
  },
  {
    title: '9. Cookies',
    content: 'Utilizamos cookies essenciais para autenticação e funcionamento da plataforma, cookies de preferência para salvar configurações de idioma e interface, e cookies analíticos (anonimizados) para melhoria do serviço. Você pode desabilitar cookies não essenciais nas configurações do seu navegador.'
  },
  {
    id: 'exclusao',
    title: '10. Dados do WhatsApp (Meta / WhatsApp Business Platform)',
    content: 'Quando uma empresa conecta seu número de WhatsApp Business à plataforma, passamos a receber da Meta as mensagens enviadas pelos clientes dessa empresa e os dados de contato associados (nome de exibição e número de telefone fornecidos pelo WhatsApp), além de identificadores técnicos da conta (ID do número e da conta WhatsApp Business). Usamos esses dados exclusivamente para viabilizar o atendimento: interpretar a mensagem do cliente, gerar a resposta do agente, agendar e confirmar compromissos, enviar lembretes e notificações por meio de modelos aprovados, e permitir a transferência para um atendente humano. Não usamos esses dados para treinar modelos de IA, não os vendemos e não os compartilhamos com terceiros além dos provedores de infraestrutura e IA descritos na seção 5. As credenciais de acesso ao WhatsApp são armazenadas de forma cifrada. O conteúdo das conversas é retido por período limitado e configurável pela empresa (até 60 dias), e os dados de sessão ativa são excluídos automaticamente após 24 horas. Para solicitar a exclusão dos dados de WhatsApp tratados por nós, a empresa ou o usuário final pode enviar um pedido para privacidade@libben.com.br; concluímos a exclusão e confirmamos por e-mail.'
  },
  {
    title: '11. Alterações nesta Política',
    content: 'Podemos atualizar esta Política a qualquer momento. Alterações materiais serão comunicadas por e-mail com 30 dias de antecedência. A versão vigente sempre estará disponível em libbenagents.com/privacy.'
  }
];

const SECTIONS_EN = [
  {
    title: '1. Who we are',
    content: 'Libben Agents is an AI-powered digital agents platform operated by FGMAX - INTERMEDIACAO DE NEGOCIOS EMPRESARIAIS E INFORMATICA LTDA, CNPJ 22.940.511/0001-26, headquartered in Joinville, Brazil. For privacy inquiries, contact privacy@libben.com.br.'
  },
  {
    title: '2. Data we collect',
    content: 'We collect data you provide during registration (name, email, phone, tax ID, billing address), platform usage data (conversations processed, agents configured, connectors used), technical data (IP address, browser type, access logs), and payment data processed by Asaas or Stripe gateways — we do not store raw card data.'
  },
  {
    title: '3. How we use your data',
    content: 'We use your data to: provide the contracted service, billing and financial management, service communications (alerts, updates, invoices), technical support, fraud prevention, and compliance with legal obligations. On paid plans, your data is never used to train AI models or sold to third parties.'
  },
  {
    title: '4. Your customers\' data (end users)',
    content: 'You are the data controller for your customers who interact with the agents. FGMAX acts as a data processor under applicable privacy regulations (LGPD, GDPR). We process this data solely to deliver the contracted service. Session data is automatically deleted after 24 hours (configurable TTL).'
  },
  {
    title: '5. Data sharing',
    content: 'We share data only with: infrastructure providers (Amazon Web Services — storage and processing), payment gateways (Asaas, Stripe), AI providers (Amazon Bedrock — message processing without data retention), and when required by law or court order. We do not sell data to third parties.'
  },
  {
    title: '6. Security',
    content: 'All data is stored on Amazon Web Services (AWS) with encryption in transit (TLS 1.2+) and at rest (AES-256). We use Amazon Cognito authentication with MFA available. We conduct periodic security audits and maintain access logs for 90 days.'
  },
  {
    title: '7. Data retention',
    content: 'Session/conversation data: automatically deleted after 24 hours (TTL). Registration and billing data: retained during the contract term and for up to 5 years after cancellation for tax and legal compliance. You may request early deletion by emailing privacy@libben.com.br.'
  },
  {
    title: '8. Your rights',
    content: 'You have the right to: access your personal data, correct inaccurate data, request deletion of your data, data portability, restrict processing, and object to processing. EU/EEA residents may also lodge a complaint with their local supervisory authority. To exercise your rights, contact privacy@libben.com.br.'
  },
  {
    title: '9. Cookies',
    content: 'We use essential cookies for authentication and platform functionality, preference cookies to save language and interface settings, and anonymized analytics cookies to improve the service. You can disable non-essential cookies in your browser settings.'
  },
  {
    id: 'exclusao',
    title: '10. WhatsApp data (Meta / WhatsApp Business Platform)',
    content: 'When a business connects its WhatsApp Business number to the platform, we receive from Meta the messages sent by that business\'s customers and the associated contact data (display name and phone number provided by WhatsApp), as well as technical account identifiers (phone number ID and WhatsApp Business Account ID). We use this data solely to enable customer service: interpreting the customer\'s message, generating the agent\'s reply, scheduling and confirming appointments, sending reminders and notifications via approved templates, and allowing handover to a human agent. We do not use this data to train AI models, we do not sell it, and we do not share it with third parties beyond the infrastructure and AI providers described in section 5. WhatsApp access credentials are stored encrypted. Conversation content is retained for a limited, business-configurable period (up to 60 days), and active session data is automatically deleted after 24 hours. To request deletion of WhatsApp data processed by us, the business or the end user may send a request to privacy@libben.com.br; we complete the deletion and confirm by email.'
  },
  {
    title: '11. Changes to this Policy',
    content: 'We may update this Policy at any time. Material changes will be communicated by email with 30 days\' notice. The current version will always be available at libbenagents.com/privacy.'
  }
];

export default function Privacy() {
  const { i18n } = useTranslation();
  const isPt = i18n.language?.startsWith('pt');
  const sections = isPt ? SECTIONS_PT : SECTIONS_EN;

  return (
    <div className="legal-page">
      <Helmet>
        <title>Política de Privacidade — Libben Agents</title>
        <meta name="description" content="Política de Privacidade da Libben Agents. Saiba como coletamos, usamos e protegemos seus dados conforme a LGPD." />
        <link rel="canonical" href="https://libbenagents.com/privacy" />
        <meta property="og:title" content="Política de Privacidade — Libben Agents" />
        <meta property="og:url" content="https://libbenagents.com/privacy" />
      </Helmet>
      <div className="legal-glow" />
      <div className="legal-container">
        <div className="legal-header">
          <Shield size={28} className="legal-icon" />
          <h1 className="legal-title">{isPt ? 'Política de Privacidade' : 'Privacy Policy'}</h1>
          <div className="legal-meta">
            <span className="legal-badge">{isPt ? 'Versão 1.1' : 'Version 1.1'}</span>
            <span className="legal-date">{isPt ? 'Última atualização: 10 de outubro de 2026' : 'Last updated: October 10, 2026'}</span>
          </div>
        </div>

        <div className="legal-sections">
          {sections.map((section, i) => (
            <div key={i} id={section.id} className="legal-section">
              <h2 className="legal-section-title">{section.title}</h2>
              <p className="legal-section-text">{section.content}</p>
            </div>
          ))}
        </div>

        <div className="legal-contact">
          <h3 className="legal-contact-title">{isPt ? 'Contato' : 'Contact'}</h3>
          <div className="legal-contact-grid">
            <a href="mailto:privacidade@libben.com.br" className="legal-contact-item">
              <Mail size={14} />
              <span>{isPt ? 'Privacidade / DPO' : 'Privacy / DPO'}</span>
              <strong>privacidade@libben.com.br</strong>
            </a>
            <a href="mailto:suporte@libben.com.br" className="legal-contact-item">
              <Mail size={14} />
              <span>{isPt ? 'Suporte Técnico' : 'Technical Support'}</span>
              <strong>suporte@libben.com.br</strong>
            </a>
          </div>
        </div>

        <p className="legal-footer">
          {isPt
            ? '© 2026 FGMAX - INTERMEDIACAO DE NEGOCIOS EMPRESARIAIS E INFORMATICA LTDA — CNPJ 22.940.511/0001-26. Todos os direitos reservados.'
            : '© 2026 FGMAX - INTERMEDIACAO DE NEGOCIOS EMPRESARIAIS E INFORMATICA LTDA — CNPJ 22.940.511/0001-26. All rights reserved.'
          }
        </p>
      </div>
    </div>
  );
}
